import { ArrowUpRight, CheckCircle2, Compass, Scale, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import PageMeta from "../components/ui/PageMeta";
import { pageCtaContent } from "../data/ctaContent";
import { aboutPageContent } from "../data/innerPages";
import { serviceJourneys, servicesProcess } from "../data/services";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1400&q=80";
const EXPERTISE_IMAGE_TOP =
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80";
const EXPERTISE_IMAGE_BOTTOM =
  "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1200&q=80";

function About() {
  const cta = pageCtaContent.about;
  const valueCards = aboutPageContent.reliabilityPoints.slice(0, 3);
  const statCards = [
    { value: aboutPageContent.whoWeHelp.length, suffix: "", label: "Core Audiences" },
    { value: serviceJourneys.length, suffix: "", label: "Admission Pathways" },
    { value: aboutPageContent.reliabilityPoints.length, suffix: "", label: "Reliability Principles" },
    { value: servicesProcess.length, suffix: "", label: "Process Stages" }
  ];

  return (
    <>
      <PageMeta title={aboutPageContent.meta.title} description={aboutPageContent.meta.description} />

      <section className="relative overflow-hidden bg-white pb-24 pt-40 sm:pt-44 lg:min-h-[900px] lg:pt-48">
        <div className="absolute inset-0 opacity-70">
          <div
            className="h-full w-full"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(116,119,127,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(116,119,127,0.08) 1px, transparent 1px)",
              backgroundSize: "60px 60px"
            }}
          />
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-6 lg:px-10">
          <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div>
              <p className="inline-flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-gold-strong">
                <span className="h-px w-10 bg-brand-gold-strong/70" />
                {aboutPageContent.hero.badge}
              </p>

              <h1 className="mt-7 text-4xl font-semibold leading-[1.02] tracking-tight text-brand-navy sm:text-6xl lg:text-[4.9rem]">
                {aboutPageContent.hero.title}
              </h1>

              <p className="mt-7 max-w-2xl text-base leading-relaxed text-brand-charcoal/76 sm:text-lg">
                {aboutPageContent.hero.description}
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-brand-navy px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-brand-gold-strong"
                >
                  Start Counselling
                </Link>
                <Link
                  to="/colleges"
                  className="inline-flex items-center gap-2 border border-brand-line bg-white px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-brand-navy transition-colors hover:border-brand-gold/50 hover:text-brand-gold-strong"
                >
                  Explore Pathways
                </Link>
              </div>

              <ul className="mt-9 space-y-2.5">
                {aboutPageContent.hero.highlights.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-brand-charcoal/76">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-strong" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative mx-auto w-full max-w-md lg:mx-0">
              <div className="absolute -right-6 -top-6 h-full w-full border-t-2 border-r-2 border-brand-gold/25" />
              <article className="relative z-10 overflow-hidden border-r-[8px] border-b-[8px] border-brand-gold-strong shadow-[0_34px_60px_-30px_rgba(10,31,68,0.45)]">
                <img
                  src={HERO_IMAGE}
                  alt="TheCareerSpace counselling environment"
                  className="h-[480px] w-full object-cover"
                  loading="eager"
                />
              </article>
              <div className="absolute -bottom-8 -left-8 z-20 flex h-20 w-20 items-center justify-center bg-brand-navy shadow-xl">
                <Compass className="h-7 w-7 text-brand-gold" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-brand-sky/25 py-28 sm:py-32 lg:py-40">
        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-6 lg:px-10">
          <div className="grid gap-16 lg:grid-cols-2">
            <article className="relative pt-16">
              <span className="absolute left-0 top-0 text-8xl italic leading-none text-brand-gold-strong/15">
                01
              </span>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-gold-strong">For Students</p>
              <h2 className="mt-6 text-4xl font-semibold leading-tight text-brand-navy sm:text-5xl">
                {aboutPageContent.studentParentView.student}
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-brand-charcoal/76">
                {aboutPageContent.whoWeHelp[0].description}
              </p>
            </article>

            <article className="relative pt-16 lg:pt-44">
              <span className="absolute left-0 top-0 text-8xl italic leading-none text-brand-gold-strong/15">
                02
              </span>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-gold-strong">For Parents</p>
              <h2 className="mt-6 text-4xl font-semibold leading-tight text-brand-navy sm:text-5xl">
                {aboutPageContent.studentParentView.parent}
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-brand-charcoal/76">
                {aboutPageContent.whoWeHelp[1].description}
              </p>
              <div className="mt-10 h-px w-full bg-brand-gold-strong/25" />
            </article>
          </div>
        </div>
      </section>

      <section className="overflow-hidden bg-white py-28 sm:py-32 lg:py-40">
        <div className="mx-auto w-full max-w-7xl px-5 sm:px-6 lg:px-10">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-5">
              <h2 className="text-4xl font-semibold leading-tight text-brand-navy sm:text-6xl">
                Reliable counselling
                <span className="block italic text-brand-gold-strong">with context first.</span>
              </h2>
              <div className="mt-7 h-1 w-28 bg-brand-gold-strong/75" />
              <p className="mt-8 text-base leading-relaxed text-brand-charcoal/76">{aboutPageContent.philosophy}</p>

              <ul className="mt-8 space-y-4">
                {aboutPageContent.reliabilityPoints.map((point) => (
                  <li
                    key={point}
                    className="flex items-start gap-2.5 rounded-lg border border-brand-navy/12 bg-brand-navy px-3 py-2.5 text-sm text-white"
                  >
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-strong" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative lg:col-span-7">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-6 pt-14">
                  <article className="border-l-4 border-brand-gold-strong bg-white p-6 shadow-[0_20px_40px_-30px_rgba(10,31,68,0.45)]">
                    <h3 className="text-xl font-semibold text-brand-navy">{aboutPageContent.whoWeHelp[2].title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-brand-charcoal/76">
                      {aboutPageContent.whoWeHelp[2].description}
                    </p>
                  </article>
                  <img
                    src={EXPERTISE_IMAGE_BOTTOM}
                    alt="Students in collaborative session"
                    className="h-72 w-full object-cover grayscale transition-all duration-500 hover:grayscale-0"
                    loading="lazy"
                  />
                </div>

                <div className="space-y-6">
                  <img
                    src={EXPERTISE_IMAGE_TOP}
                    alt="Counselling strategy discussion"
                    className="h-72 w-full object-cover"
                    loading="lazy"
                  />
                  <article className="border-l-4 border-brand-gold-strong bg-brand-navy p-6 shadow-[0_20px_40px_-30px_rgba(10,31,68,0.45)]">
                    <h3 className="text-xl font-semibold text-white">Explore Admission Pathways</h3>
                    <ul className="mt-4 space-y-2.5">
                      {serviceJourneys.slice(0, 3).map((service) => (
                        <li key={service.key}>
                          {service.external ? (
                            <a
                              href={service.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-sm text-white transition-colors hover:text-brand-gold-soft"
                            >
                              {service.title}
                              <ArrowUpRight className="h-3.5 w-3.5" />
                            </a>
                          ) : (
                            <Link
                              to={service.to}
                              className="inline-flex items-center gap-1.5 text-sm text-white transition-colors hover:text-brand-gold-soft"
                            >
                              {service.title}
                              <ArrowUpRight className="h-3.5 w-3.5" />
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </article>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-brand-navy py-28 sm:py-32">
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "url('https://www.transparenttextures.com/patterns/carbon-fibre.png')" }} />
        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-6 lg:px-10">
          <div className="text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-gold">Foundation</p>
            <h2 className="mt-4 text-4xl font-semibold italic text-white sm:text-6xl">Core Values</h2>
          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {valueCards.map((value, index) => {
              const icons = [ShieldCheck, Scale, Compass];
              const Icon = icons[index] || ShieldCheck;
              const headings = ["Transparency", "Precision", "Continuity"];

              return (
                <article key={value} className="border-t-2 border-brand-gold bg-brand-navy-deep p-8 transition-transform duration-300 hover:-translate-y-1">
                  <Icon className="h-7 w-7 text-brand-gold" />
                  <h3 className="mt-6 text-2xl font-semibold text-brand-gold">{headings[index]}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-white">{value}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto grid w-full max-w-7xl gap-10 px-5 sm:grid-cols-2 sm:px-6 lg:grid-cols-4 lg:px-10">
          {statCards.map((stat) => (
            <article key={stat.label}>
              <p className="flex items-end text-6xl font-semibold leading-none text-brand-navy">
                {stat.value}
                {stat.suffix ? <span className="mb-1 text-2xl text-brand-gold-strong">{stat.suffix}</span> : null}
              </p>
              <div className="mt-3 h-1 w-12 bg-brand-gold-strong/75 transition-all duration-500 hover:w-full" />
              <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-charcoal/72">
                {stat.label}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden bg-brand-navy-deep py-28 sm:py-32 lg:py-40">
        <div className="absolute inset-0 opacity-10">
          <div
            className="h-full w-full"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(116,119,127,0.12) 1px, transparent 1px), linear-gradient(to bottom, rgba(116,119,127,0.12) 1px, transparent 1px)",
              backgroundSize: "60px 60px"
            }}
          />
        </div>
        <div className="relative z-10 mx-auto w-full max-w-5xl px-5 text-center sm:px-6 lg:px-10">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-gold">{cta.badge}</p>
          <h2 className="mt-6 text-4xl font-semibold leading-tight text-white sm:text-6xl lg:text-7xl">
            {cta.title}
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white">
            {cta.description}
          </p>

          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to={cta.primaryCta.to}
              className="inline-flex items-center justify-center bg-brand-gold px-10 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-brand-navy transition-all duration-300 hover:bg-white"
            >
              {cta.primaryCta.label}
            </Link>
            <Link
              to={cta.secondaryCta.to}
              className="inline-flex items-center justify-center border border-white/25 px-10 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:border-brand-gold hover:text-brand-gold"
            >
              {cta.secondaryCta.label}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

export default About;
